import type { SchoolType, Survey, SurveyResults, SurveySelection } from 'app/common/types/surveys'
import SimpleDate from 'app/common/classes/simple-date'

export const apiDateFormat = 'YYYY-MM-DD' as const

type OptionDto = { id: number | string; title: string; district_id?: number | null; city_id?: number | null }
type SelectionDto = Array<OptionDto> | { count: number; items: Array<OptionDto> }

export type SurveyDto = {
  id: number
  title: string
  city_id?: number | null
  city_name?: string | null
  district_id?: number | null
  district_name?: string | null
  school_type?: Exclude<SchoolType, ''> | null
  school_type_label?: string | null
  school_type_short_label?: string | null
  school_id?: number | null
  school_name?: string | null
  city_ids?: Array<number>
  district_ids?: Array<number>
  school_ids?: Array<number>
  cities?: SelectionDto
  districts?: SelectionDto
  schools?: SelectionDto
  survey_kind?: string
  survey_kind_label?: string
  survey_location?: string
  survey_location_label?: string
  starts_on: string
  ends_on: string
  active: boolean
  deleted?: boolean
  results?: SurveyResults | null
}

const convertSelection = (selection?: SelectionDto, id?: number | null, title?: string | null): SurveySelection => {
  const items = Array.isArray(selection) ? selection : selection?.items
  const converted = (items || (id != null && title ? [{ id, title }] : [])).map(item => ({
    id: String(item.id),
    title: item.title,
    districtId: item.district_id == null ? undefined : String(item.district_id),
    cityId: item.city_id == null ? undefined : String(item.city_id),
  }))
  return { count: selection && !Array.isArray(selection) ? selection.count : converted.length, items: converted }
}

export const convertSurvey = (survey: SurveyDto): Survey => {
  const cities = convertSelection(survey.cities, survey.city_id, survey.city_name)
  const districts = convertSelection(survey.districts, survey.district_id, survey.district_name)
  const schools = convertSelection(survey.schools, survey.school_id, survey.school_name)
  return {
    id: String(survey.id),
    title: survey.title,
    startsOn: new SimpleDate(survey.starts_on, apiDateFormat),
    endsOn: new SimpleDate(survey.ends_on, apiDateFormat),
    active: survey.active,
    deleted: survey.deleted,
    surveyKind: survey.survey_kind,
    surveyKindLabel: survey.survey_kind_label,
    surveyLocation: survey.survey_location,
    surveyLocationLabel: survey.survey_location_label,
    schoolTypeShortLabel: survey.school_type_short_label || (({ School: 'ОУ', Preschool: 'ДОУ', HighSchool: 'ВУЗы' } as Partial<Record<SchoolType, string>>)[survey.school_type || ''] || undefined),
    cities,
    districts,
    schools,
    scope: {
      cityIds: survey.city_ids?.map(String) || cities.items.map(item => item.id),
      districtIds: survey.district_ids?.map(String) || districts.items.map(item => item.id),
      schoolIds: survey.school_ids?.map(String) || schools.items.map(item => item.id),
      cityId: cities.count === 1 ? cities.items[0]?.id : undefined,
      cityName: cities.count === 1 ? cities.items[0]?.title : undefined,
      districtId: districts.count === 1 ? districts.items[0]?.id : undefined,
      districtName: districts.count === 1 ? districts.items[0]?.title : undefined,
      schoolType: survey.school_type || undefined,
      schoolTypeLabel: survey.school_type_label || undefined,
      schoolId: schools.count === 1 ? schools.items[0]?.id : undefined,
      schoolName: schools.count === 1 ? schools.items[0]?.title : undefined,
    },
    results: survey.results || { total: 0, positive: 0, negative: 0 },
  }
}
